import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { AlertCircle, MapPin, User, FileText, CheckCircle2, Navigation, Mail, CreditCard } from "lucide-react";
import { useConsumerDetail } from '../../../shared/hook';
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useTranslation } from "react-i18next";

export function AccountInfoTab() {
  const { t } = useTranslation();
  const { data: consumerDetails } = useConsumerDetail({
    remote_utility_id: getRemoteUtilityId(),
    consumer_no: getLoginDataFromStorage().remoteConsumerNumber,
  });

  const result = consumerDetails?.result || {};

  const fullName = `${result.firstName || ''} ${result.lastName || ''}`.trim() || t("N/A");
  const email = result.email || t("N/A");
  const phone = result.contactNumber || t("N/A");

  const category = result.categoryDisplay || t("N/A");
  const subCategory = result.subCategoryDisplay || t("N/A");
  const accountType = result.categoryDisplay || t("N/A");
  const status = result.statusDisplay || t("N/A");

  const activeServices = result.utilityService || [];

  const getAddressField = (addr: any, key: string) => {
    if (!addr) return '';
    return addr[key] || addr[key.toUpperCase()] || addr[key.toLowerCase()] || '';
  };

  const formatAddress = (addr: any) => {
    if (!addr) return 'N/A';
    const parts = [
      getAddressField(addr, 'address'),
      getAddressField(addr, 'unit'),
      getAddressField(addr, 'city'),
      getAddressField(addr, 'zipcode')
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : t("N/A");
  };

  const serviceAddress = formatAddress(result.addressData?.service);
  const billingAddress = formatAddress(result.addressData?.billing);

  const isAutoPay = result.isAutoPay ? t("Enabled") : t("Disabled");
  const currentBalance = result.creditAvailable !== undefined ? `$${Number(result.creditAvailable).toFixed(2)}` : t("N/A");

  return (
    <div className="flex flex-col gap-5 pt-5">
      {/* Account Holder Information */}
      <Card className="shadow-sm border-gray-200 overflow-hidden">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-3 px-6">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-gray-700">
            <User className="w-4 h-4 text-blue-500" /> {t("Account Holder Information")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-gray-400" /> {t("Full Name")}
              </p>
              <p className="font-medium text-gray-900">{fullName}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-gray-400" /> {t("Email Address")}
              </p>
              <p className="font-medium text-gray-900">{email}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-gray-400" /> {t("Phone Number")}
              </p>
              <p className="font-medium text-gray-900">{phone}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Classification */}
      <Card className="shadow-sm border-gray-200 overflow-hidden">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-3 px-6">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-gray-700">
            <FileText className="w-4 h-4 text-blue-500" /> {t("Account Classification")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-gray-400" /> {t("Category")}
              </p>
              <p className="font-medium text-gray-900">{category}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1.5">
                <Navigation className="w-3.5 h-3.5 text-gray-400" /> {t("Sub-Category")}
              </p>
              <p className="font-medium text-gray-900">{subCategory}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-gray-400" /> {t("Account Type")}
              </p>
              <p className="font-medium text-gray-900">{accountType}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-gray-400" /> {t("Status")}
              </p>
              <p className="font-medium text-gray-900">{status}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Services */}
      <Card className="shadow-sm border-gray-200 overflow-hidden">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-3 px-6">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-gray-700">
            <CheckCircle2 className="w-4 h-4 text-blue-500" /> {t("Active Services")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-3">
            {activeServices.length > 0 ? (
              activeServices.map((service: string, i: number) => (
                <Badge key={i} variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 px-3 py-1 font-medium gap-1.5 rounded-full text-sm">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {service}
                </Badge>
              ))
            ) : (
              <p className="text-sm text-gray-500">{t("No active services")}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Service & Billing Address */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card className="shadow-sm border-gray-200 overflow-hidden">
          <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-3 px-6">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-gray-700">
              <MapPin className="w-4 h-4 text-blue-500" /> {t("Service Address")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="font-medium text-gray-900 mb-1">{serviceAddress}</p>
            <p className="text-xs text-gray-500">{t("Physical location where service is delivered")}</p>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-gray-200 overflow-hidden">
          <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-3 px-6">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-gray-700">
              <Mail className="w-4 h-4 text-blue-500" /> {t("Billing Address")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="font-medium text-gray-900 mb-1">{billingAddress}</p>
            <p className="text-xs text-gray-500">{t("Where bills and correspondence are mailed")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Billing Summary */}
      <Card className="shadow-sm border-gray-200 overflow-hidden">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-3 px-6">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-gray-700">
            <CreditCard className="w-4 h-4 text-blue-500" /> {t("Billing Summary")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">{t("Current Balance")}</p>
              <p className="font-medium text-gray-900">{currentBalance}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">{t("Next Due Date")}</p>
              <p className="font-medium text-gray-900">{t("N/A")}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">{t("Last Payment")}</p>
              <p className="font-medium text-gray-900">{t("N/A")}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">{t("Autopay")}</p>
              <p className="font-medium text-gray-900">{isAutoPay}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
